import { Grid, Typography } from '@material-ui/core/';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import { get, orderBy, isEmpty, omit } from 'lodash';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Button,
  crudGetList,
  TopToolbar,
  useDataProvider
  //useWhyDidYouUpdate
} from 'react-admin';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';

import EntitlementApi from '../../../core/apis/EntitlementApi';
import { useFetch } from '../../../core/hooks/UseFetch';
import AddAuthorizationDialog from './AddAuthorizationDialog';
import UserAuthorizationUpdate from './UserAuthorizationUpdate';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import ForceListRender from '../../../core/components/forked/list/ForceListRender';
import ImportPermissionGroupsDialog from './importPermissionsGroups/ImportPermissionsGroupsDialog';
import { ImportPermissionsProvider } from './importPermissionsGroups/ImportPermissionsContext';
import Confirm from '../../../core/components/forked/layout/Confirm';
import { USERTYPE } from '../EnumUserType';

const useUserAuthorizationTopToolbarStyles = makeStyles({
  headline: {
    paddingTop: 0
  },
  topToolbar: {
    paddingTop: 0,
    paddingBottom: 0
  },
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    alignSelf: 'center'
  },
  importPermissionButton: {
    border: '1px solid #E0E0E0',
    marginRight: '6px',
    background: '#FFFFFF',
    alignSelf: 'center'
  },
  paragraph: {
    fontSize: '14px'
  },
  radioLabel: {
    fontSize: '14px'
  },
  radioLabelRoot: {
    marginRight: '0px !important'
  },
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block'
  }
});

const UserAuthorizationTopToolbar = props => {
  const {
    userType,
    groupsFilter,
    onGroupsFilterChange,
    empty,
    hasWriteAuthorizationPermission,
    handleAddAuthorization,
    ids,
    id,
    authorizations,
    usedIds
  } = props;
  const classes = useUserAuthorizationTopToolbarStyles();
  const [openDialog, setOpenDialog] = useState(false);
  const userTypeRoleCheck =
    userType === USERTYPE.regularContact || userType === USERTYPE.investorAdmin;
  const [openImportPermissionGroups, setOpenImportPermissionGroups] = useState(
    false
  );
  const [
    openWarningConfirmForImport,
    setOpenWarningConfirmForImport
  ] = useState(false);

  const [
    topToolBarClasses,
    formCtrlLabel1Cls,
    formCtrlLabel2Cls
  ] = useMemo(
    () => [
      { root: classes.topToolbar },
      { label: classes.radioLabel },
      { root: classes.radioLabelRoot, label: classes.radioLabel }
    ],
    [classes]
  );

  return (
    <Grid container>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="h5" className={classes.headline}>
          Access Rights
        </Typography>
        {hasWriteAuthorizationPermission && (
          <TopToolbar classes={topToolBarClasses}>
            <Button
              onClick={() => {
                // check if there are already userauthorizations assigned to the current user, if ys trigger the confirm warning modal
                if (ids.length > 0) {
                  setOpenWarningConfirmForImport(true);
                } else {
                  setOpenImportPermissionGroups(true);
                }
              }}
              label="Import Permission Groups"
              variant="outlined"
              color="primary"
              className={classes.importPermissionButton}
            />
            <ImportPermissionsProvider
              isRegular={userTypeRoleCheck}
              idsFromTheUserToUpdate={ids}
              id={id}
              authorizations={authorizations}
              hasWriteAuthorizationPermission={hasWriteAuthorizationPermission}
            >
              <ImportPermissionGroupsDialog
                open={openImportPermissionGroups}
                setOpenImportPermissionGroups={setOpenImportPermissionGroups}
              />
            </ImportPermissionsProvider>
            <Button
              onClick={() => setOpenDialog(true)}
              label={userTypeRoleCheck ? 'Add Investor' : 'Add Fund'}
              variant="outlined"
              color="primary"
              className={classes.addButton}
            />
            {openDialog && (
              <AddAuthorizationDialog
                userTypeRoleCheck={userTypeRoleCheck}
                handleAddAuthorization={handleAddAuthorization}
                setOpenDialog={setOpenDialog}
                usedIds={usedIds}
                creation={false}
                {...omit(props, ['onGroupsFilterChange'])}
              />
            )}
          </TopToolbar>
        )}
      </Grid>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <span className={classes.paragraph}>
            Here you can view and change access rights. Tick checkboxes to
            assign Group Permissions.
          </span>
        </Grid>
        <Grid item>
          <RadioGroup
            aria-label="groupsFilter"
            name="groupsFilter"
            value={groupsFilter}
            onChange={onGroupsFilterChange}
            row
          >
            <FormControlLabel
              value="ALL"
              control={<Radio color="primary" id="show-all" />}
              label="Show All"
              classes={formCtrlLabel1Cls}
              id="show-all"
            />
            <FormControlLabel
              value="ONLY_ASSIGNED"
              control={<Radio color="primary" id="show-assigned" />}
              label="Show Assigned"
              classes={formCtrlLabel2Cls}
            />
          </RadioGroup>
        </Grid>
      </Grid>
      <Confirm
        isOpen={openWarningConfirmForImport}
        title="Warning"
        content="All Permission Groups assigned to this Contact will be merged with the ones you import. Do you wish to continue?"
        onConfirm={() => {
          setOpenWarningConfirmForImport(false);
          setOpenImportPermissionGroups(true);
        }}
        onClose={() => setOpenWarningConfirmForImport(false)}
      />
      {empty && (
        <Grid item>
          <span className={classes.label}>
            There are no Access Rights to be displayed
          </span>
        </Grid>
      )}
    </Grid>
  );
};

/**
 * Component used to get user authorizations for the connected user, not for super admin
 * @param {*} props
 */
const UserAuthorizationUpdateComponentWithAuhtorizations = props => {
  const { userProfile } = props;
  const { data = [] } = useFetch(
    EntitlementApi.getAuthorizationsByUserId,
    userProfile.userId
  );

  const authorizationsConnectedUser = useMemo(
    () =>
      (data || []).map(authorization => ({
        ...authorization,
        // id: investorId:fundId:groupId
        id: `${authorization.investorId}:${authorization.fundId}:${authorization.groupId}`
      })),
    [data]
  );

  return (
    <UserAuthorizationUpdate
      {...props}
      authorizationsConnectedUser={authorizationsConnectedUser}
    />
  );
};

const useProgressStyles = makeStyles({
  progress: {
    display: 'flex',
    justifyContent: 'center'
  }
});

/**
 * Display the circular progress component
 */
const Progress = () => {
  const classes = useProgressStyles();

  return (
    <div className={classes.progress}>
      <CircularProgress />
    </div>
  );
};

/**
 * Component used to wait the profile of the user connected
 * And use the right component when user authorizations are needed or not
 * @param {*} props
 */
const UserAuthorizationUpdateComponentProfile = props => {
  const {
    userProfile,
    data,
    setAuthorizations,
    loaded,
    loading,
    groups = [],
    ids
  } = props;

  const groupIds = useMemo(() => groups.map(({ id }) => id), [groups]);

  // Filter authorizations for this user and for the non admin groups
  const authorizations = useMemo(() => {
    if (loaded && !loading && groupIds.length && ids.length) {
      return Object.values(data).filter(
        item =>
          item.userId === props.id &&
          ids.includes(item.id) &&
          groupIds.includes(item.groupId)
      );
    }
    return [];
  }, [loaded, loading, data, ids, groupIds, props.id]);

  useEffect(() => setAuthorizations(authorizations), [
    authorizations,
    setAuthorizations
  ]);

  if (!isEmpty(userProfile)) {
    const UserAuthorizationUpdateComponent =
      userProfile.userType === USERTYPE.superAdmin
        ? UserAuthorizationUpdate
        : UserAuthorizationUpdateComponentWithAuhtorizations;

    return (
      <UserAuthorizationUpdateComponent
        {...omit(props, ['data'])}
        authorizations={authorizations}
      />
    );
  } else {
    return <Progress />;
  }
};

const updateUsedIds = (ids, auth) => {
  if (auth.fundId) {
    ids.funds = ids.funds || {};
    ids.funds[auth.fundId] = true;
  }
};

const useUserAuthorizationStyles = makeStyles({
  gridTab: {
    marginTop: '-17px !important'
  },
  mainList: {
    overflow: 'hidden'
  }
});

const noMarginStyle = { margin: '0px', width: '100%' };
const emptyObject = {};

const UserAuthorizationTab = props => {
  const {
    userType,
    crudGetList, //NOSONAR javascript:S1117
    onChangeAuthorizations,
    permissions,
    groups,
    funds,
    whenBlockNavigation,
    location,
    ...rest
  } = props;
  // when perf debugging, check what's changed in the properties
  //useWhyDidYouUpdate('UserAuthorizationTab', props);

  const classes = useUserAuthorizationStyles();
  const notifyServerError = useNotifyServerError();

  const isRegular =
    userType === USERTYPE.regularContact || userType === USERTYPE.investorAdmin;

  const mainClasses = useMemo(() => ({ main: classes.mainList }), [classes]);
  const listFilter = useMemo(() => ({ userId: props.id }), [props.id]);

  const hasWriteAuthorizationPermission = useMemo(() => {
    return permissions.includes('AUTH_WRITE_USER_AUTHORIZATION');
  }, [permissions]);

  const dataProvider = useDataProvider();

  const [newAuthorizations, setNewAuthorizations] = useState([]);
  const [groupsFilter, setGroupsFilter] = useState('ALL');
  const [authorizationsDirty, setAuthorizationsDirty] = useState(false);
  const [newAuthorizationsDirty, setNewAuthorizationsDirty] = useState(false);
  const [authorizations, setAuthorizations] = useState([]);

  useEffect(() => {
    // Prevent navigation, if there are unsaved authorizations or new unsaved authorizations
    if (
      !whenBlockNavigation &&
      (authorizationsDirty || newAuthorizationsDirty)
    ) {
      onChangeAuthorizations(true);
    }

    // Allow navigation, when there is no more unsaved changes
    if (
      whenBlockNavigation &&
      !authorizationsDirty &&
      !newAuthorizationsDirty
    ) {
      onChangeAuthorizations(false);
    }
  }, [
    whenBlockNavigation,
    authorizationsDirty,
    newAuthorizationsDirty,
    onChangeAuthorizations
  ]);

  useEffect(() => {
    // Refresh the list of "groups"
    dataProvider
      .getList('groups', {
        pagination: { page: 1, perPage: -1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { adminGroup: false, status: 'ACTIVE' }
      })
      .catch(notifyServerError);
  }, [dataProvider, notifyServerError]);

  const refreshList = useCallback(() => {
    // Refresh the list
    dataProvider
      .getList('userauthorizations', {
        pagination: { page: 1, perPage: -1 },
        sort: {},
        filter: { userId: props.id }
      })
      .catch(notifyServerError);
  }, [dataProvider, props.id, notifyServerError]);

  const handleAddAuthorization = useCallback(
    newValues => setNewAuthorizations(newAuths => newAuths.concat(newValues)),
    []
  );

  const onGroupsFilterChange = useCallback(
    event => setGroupsFilter(event.target.value),
    []
  );

  const finalGroups = useMemo(() => {
    const groupsArray = Object.values(groups);
    const filteredGroups = groupsArray.filter(item => {
      return item.status === 'ACTIVE' && !item.adminGroup;
    });
    return orderBy(filteredGroups, item => item.name.toLowerCase(), ['ASC']);
  }, [groups]);

  const finalFunds = useMemo(() => {
    return Object.values(funds);
  }, [funds]);

  const usedIds = useMemo(() => {
    const ids = {};
    Object.values(authorizations).forEach(auth => updateUsedIds(ids, auth));
    newAuthorizations.forEach(auth => updateUsedIds(ids, auth));
    return ids;
  }, [authorizations, newAuthorizations]);

  const actionsToolbarProps = {
    userType,
    groupsFilter,
    onGroupsFilterChange,
    handleAddAuthorization,
    hasWriteAuthorizationPermission,
    usedIds,
    isRegular,
    id: rest.id,
    authorizations
  };

  return (
    <ForceListRender
      style={noMarginStyle}
      classes={mainClasses}
      filter={listFilter}
      sort={emptyObject}
      pagination={null}
      perPage={-1}
      hasList
      hasEdit={false}
      hasShow={false}
      hasCreate={false}
      bulkActionButtons={false}
      forceShowList={!!newAuthorizations.length} // React-Admin 3.17+ ListView renders an empty page if total fetched = 0, we need to let it know that we have things to render
      basePath="/userauthorizations"
      resource="userauthorizations"
      title=" "
      actions={<UserAuthorizationTopToolbar {...actionsToolbarProps} />}
      empty={<UserAuthorizationTopToolbar {...actionsToolbarProps} empty />}
    >
      <UserAuthorizationUpdateComponentProfile
        {...rest}
        userType={userType}
        newAuthorizations={newAuthorizations}
        groupsFilter={groupsFilter}
        onChangeAuthorizations={setAuthorizationsDirty}
        onChangeNewAuthorizations={setNewAuthorizationsDirty}
        hasWriteAuthorizationPermission={hasWriteAuthorizationPermission}
        groups={finalGroups}
        refreshList={refreshList}
        funds={finalFunds}
        setAuthorizations={setAuthorizations}
      />
    </ForceListRender>
  );
};

const mapStateToProps = state => {
  return {
    groups: get(state, 'admin.resources.groups.data', {}),
    funds: get(state, 'admin.resources.funds.data', {})
  };
};

const mapDispatchToProps = {
  crudGetList
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserAuthorizationTab);
